import { Core, Decorative } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { Opacity } from "@faire/web--source/slate/Color";
import { Input } from "@faire/web--source/slate/Input";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { eventsThemeColors } from "@faire/web--source/slate/Theme/eventsThemeColors";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import { trackBrandLeadsApplyToSellOnFaireClick } from "@faire/web-api--source/events/brandLeads/click/applyToSellOnFaire";
import { trackBrandLeadsBrandsPageSignInClick } from "@faire/web-api--source/events/brandLeads/click/brandsPageSignIn";
import { trackBrandLeadsScheduleACallClick } from "@faire/web-api--source/events/brandLeads/click/scheduleACall";
import brands from "@faire/web-api--source/routes/brands";
import { observer } from "mobx-react";
import React from "react";
import styled, { css, keyframes } from "styled-components";

import { FaireMarketIcon } from "@faire/retailer/features/FaireMarkets/FaireMarketIcon";
import { useMarketStore } from "@faire/retailer/stores/domain/Events/FaireMarkets/MarketStore";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";

import { CTAPosition, StyledSellOnFaireButton } from "./const";
import { useBrandsLandingPageState } from "./hooks/useBrandsLandingPageState";

const ANIMATION_LENGTH = 300;
const NAV_HEIGHT_DESKTOP = 82;

const BrandsLandingPageNavBase: React.FC = () => {
  const { showBrandLandingPage: displayFaireMarket } = useMarketStore([
    "showBrandLandingPage",
  ]);
  const {
    showBanner,
    isPersonalized,
    calendlyLink,
    emailAddress,
    handleEmailChange,
    goToForm,
    isMarketSectionVisible,
  } = useBrandsLandingPageState();

  if (!showBanner) {
    return null;
  }

  const Prompt = isPersonalized ? (
    <PersonalizedPrompt
      calendlyLink={calendlyLink}
      displayFaireMarket={displayFaireMarket}
    />
  ) : (
    <UnpersonalizedPrompt
      emailAddress={emailAddress}
      displayFaireMarket={displayFaireMarket}
      handleEmailChange={handleEmailChange}
      goToForm={goToForm}
    />
  );

  return (
    <BrandsLandingPageSignUpNav $isFaireMarket={displayFaireMarket}>
      <ResponsiveContentV2
        tabletAndBelow={
          <Column>
            {displayFaireMarket && (
              <>
                <MobileOrTabletBody
                  isMarketSectionVisible={isMarketSectionVisible}
                />
                <Spacer size="1x" />
              </>
            )}
            {Prompt}
          </Column>
        }
        desktopAndAbove={
          <FullWidthRow align="center" justify="space-between">
            <DesktopBody
              displayFaireMarket={displayFaireMarket}
              isMarketSectionVisible={isMarketSectionVisible}
            />
            {Prompt}
          </FullWidthRow>
        }
      />
    </BrandsLandingPageSignUpNav>
  );
};

export const BrandsLandingPageNav = observer(BrandsLandingPageNavBase);

const PersonalizedPromptBase: React.FC<{
  calendlyLink: string;
  displayFaireMarket: boolean;
}> = ({ calendlyLink, displayFaireMarket }) => {
  return (
    <Row align="center" justify="center">
      <StyledSellOnFaireButton
        style={{ width: "max-content", minWidth: "200px" }}
        linkTo={calendlyLink}
        target="_blank"
        onClick={() => {
          trackBrandLeadsScheduleACallClick(CTAPosition.STICKY_BANNER);
        }}
      >
        <StrictLocalMsg
          defaultMessage="Schedule a Call"
          description={{
            text: "Have the prospective client schedule a call with a Faire salesperson",
          }}
        />
      </StyledSellOnFaireButton>
      <Spacer size="4x" />
      <Button
        tertiaryColor={displayFaireMarket ? "faireBlack" : "white"}
        onClick={() =>
          trackBrandLeadsBrandsPageSignInClick(CTAPosition.STICKY_BANNER)
        }
        linkTo={brands({ signIn: "1" })}
      >
        <StrictLocalMsg defaultMessage="Sign In" />
      </Button>
    </Row>
  );
};

const PersonalizedPrompt = observer(PersonalizedPromptBase);

const UnpersonalizedPromptBase: React.FC<{
  emailAddress: string | undefined;
  displayFaireMarket: boolean;
  handleEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  goToForm: () => void;
}> = ({ emailAddress, displayFaireMarket, handleEmailChange, goToForm }) => {
  const { strictLocalize } = useStrictLocalization();
  const { isEurope } = useRetailerStore(["isEurope"]);

  const handleGoToFormClickHandler = () => {
    trackBrandLeadsApplyToSellOnFaireClick(CTAPosition.STICKY_BANNER);
    goToForm();
  };

  return (
    <Row justify="center">
      <NavEmailInputFieldWrapper>
        <NavEmailInputField
          placeholder={strictLocalize({
            defaultMessage: "Email address",
            description: {
              text: "Input placeholder. Type in email address in this field",
            },
          })}
          value={emailAddress}
          autoComplete="email"
          onInput={handleEmailChange}
          $isFaireMarket={displayFaireMarket}
        />
      </NavEmailInputFieldWrapper>
      <NavApplyButton
        onClick={handleGoToFormClickHandler}
        variant={displayFaireMarket ? "primary" : "secondary"}
        $isFaireMarket={displayFaireMarket}
      >
        {isEurope ? (
          <StrictLocalMsg
            defaultMessage="Get Started"
            description={{
              text: "CTA for prospective brand to get started signing up to sell on Faire",
            }}
          />
        ) : (
          <StrictLocalMsg
            defaultMessage="Start Selling"
            description={{
              text: "CTA for prospective brand to get started signing up to sell on Faire",
            }}
          />
        )}
      </NavApplyButton>
    </Row>
  );
};

const UnpersonalizedPrompt = observer(UnpersonalizedPromptBase);

const DesktopBodyBase: React.FC<{
  displayFaireMarket: boolean;
  isMarketSectionVisible: boolean;
}> = ({ displayFaireMarket, isMarketSectionVisible }) => {
  const { isEurope } = useRetailerStore(["isEurope"]);
  const { marketName } = useMarketStore(["marketName"]);

  if (displayFaireMarket) {
    return (
      <Row align="center">
        <StyledIcon $isMarketSectionVisible={isMarketSectionVisible} />
        <Spacer width="2x" />
        <Typography variant="paragraphSansMedium">
          <StrictLocalMsg
            defaultMessage="Join Faire in time to sell at {marketEventName}"
            values={{
              marketEventName: marketName,
            }}
          />
        </Typography>
      </Row>
    );
  } else if (isEurope) {
    return (
      <>
        <MaxWidthTypography
          variant="subheadingSansMedium"
          color={Core.text.inverse}
        >
          <StrictLocalMsg
            defaultMessage="Sell to hundreds of thousands of retailers across Europe and the U.S."
            description={{
              text: "Promotional message imploring brands to join Faire to be able to sell to the large number of retailers on Faire.",
              img: "https://cdn.faire.com/fastly/a79d452c36396a89225c8978ca1656b17c76ffa96494a0955d2bfa31a0068e71.png",
            }}
          />
        </MaxWidthTypography>
        <Spacer width="10x" />
      </>
    );
  }
  return (
    <>
      <MaxWidthTypography
        variant="subheadingSansMedium"
        color={Core.text.inverse}
      >
        <StrictLocalMsg
          defaultMessage="Access hundreds of thousands of retailers around the world"
          description={{
            text: "Promotional message imploring brands to join Faire to be able to sell to the large number of retailers on Faire.",
            img: "https://cdn.faire.com/fastly/2aa3a335a3b25456463744f210c27d33db7801f95211cb43bff2c7949b0450dd.png",
          }}
        />
      </MaxWidthTypography>
      <Spacer width="10x" />
    </>
  );
};

const DesktopBody = observer(DesktopBodyBase);

const MobileOrTabletBodyBase: React.FC<{
  isMarketSectionVisible: boolean;
}> = ({ isMarketSectionVisible }) => {
  const { marketName } = useMarketStore(["marketName"]);

  return (
    <>
      <Row align="center">
        <StyledIcon $isMarketSectionVisible={isMarketSectionVisible} />
        <Spacer width="2x" />
        <Typography variant="paragraphSansMedium">
          <StrictLocalMsg
            defaultMessage="Join Faire in time to sell at {marketEventName}"
            values={{
              marketEventName: marketName,
            }}
          />
        </Typography>
      </Row>
      <Spacer height="1x" />
    </>
  );
};

const MobileOrTabletBody = observer(MobileOrTabletBodyBase);

const MaxWidthTypography = styled(Typography)`
  max-width: 600px;
`;

const slideIn = keyframes`
    0% {
        top: -${NAV_HEIGHT_DESKTOP}px;
    }
    100% {
        top: 0;
    }
`;

const BrandsLandingPageSignUpNav = styled.nav<{ $isFaireMarket?: boolean }>`
  position: fixed;
  height: fit-content;
  width: 100vw;
  background-color: ${({ $isFaireMarket }) =>
    $isFaireMarket
      ? eventsThemeColors.faireMarket.lightBackground
      : Decorative.shade.yellow};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  z-index: 12;
  min-height: ${NAV_HEIGHT_DESKTOP}px;
  top: 0;
  animation: ${slideIn} ${ANIMATION_LENGTH}ms ease-in-out 0s;
  padding: ${getSpacing(0, "5x")};

  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("2x")};
  }
  ${responsiveMediaQueries.tablet} {
    padding: ${getSpacing("2x", "3x")};
  }
`;

const NavEmailInputFieldWrapper = styled(Row)`
  width: 280px;
  height: 50px;

  ${responsiveMediaQueries.tabletAndBelow} {
    width: 200px;
  }
`;

const NavEmailInputField = styled(Input)<{ $isFaireMarket?: boolean }>`
  width: 280px !important;
  height: 50px !important;
  border: 1.2px solid
    ${({ $isFaireMarket }) =>
      $isFaireMarket ? Core.border.default : Core.border.muted} !important;
  background: none !important;

  ${responsiveMediaQueries.tabletAndBelow} {
    width: 200px !important;
  }

  ${responsiveMediaQueries.mobile} {
    height: 40px !important;
    margin-top: 5px !important;
  }

  input {
    ${({ $isFaireMarket }) =>
      $isFaireMarket
        ? css`
            color: ${Core.text.default};
            &::placeholder {
              color: ${Core.text.default};
            }
          `
        : css`
            color: ${Core.text.inverse};
            &::placeholder {
              color: ${Core.text.inverse};
            }
          `}
  }

  &:focus,
  &:focus-within {
    ${({ $isFaireMarket }) =>
      $isFaireMarket
        ? css`
            border-color: ${Core.text.default};
            input {
              &::placeholder {
                color: ${Core.text.default + Opacity.O_50};
              }
            }
          `
        : css`
            border-color: ${Core.border.muted};
            input {
              &::placeholder {
                color: ${Core.text.inverse + Opacity.O_50};
              }
            }
          `}
  }
`;

const NavApplyButton = styled(Button)<{ $isFaireMarket?: boolean }>`
  min-width: 130px;
  height: 50px;
  width: max-content;
  ${({ $isFaireMarket }) =>
    $isFaireMarket
      ? css`
          color: ${Core.text.inverse};
          background-color: ${Core.surface.default};
          border: 1.2px solid ${Core.border.default};
        `
      : css`
          color: ${Core.text.default};
          background-color: ${Core.surface.inverse};
          border: 1.2px solid ${Core.border.muted};
        `}

  ${responsiveMediaQueries.mobile} {
    min-width: 110px;
    margin-top: 5px;
    width: 100%;
    height: 40px;
  }
`;

const FullWidthRow = styled(Row)`
  width: 100%;
`;

const StyledIcon = styled(FaireMarketIcon).attrs({
  size: "32px",
})<{ $isMarketSectionVisible: boolean }>`
  ${({ $isMarketSectionVisible }) =>
    $isMarketSectionVisible
      ? css`
          animation: rotation 4s infinite linear;

          @keyframes rotation {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(359deg);
            }
          }
        `
      : null}
  ${responsiveMediaQueries.mobile} {
    align-self: start;
  }
`;
